import * as React from 'react';

/**
 * Keeps a component mounted for `delayMs` after `isOpen` flips to `false`, so an
 * exit animation (e.g. a Blueprint `Drawer`/`Dialog` close transition) can play
 * out before the component unmounts.
 *
 * This lets an overlay be conditionally rendered — so its (potentially
 * expensive) component body doesn't run while it's closed — without losing the
 * close animation that an unconditional unmount would cut short. Returns `false`
 * until the first open, so overlays that are closed on mount never render.
 *
 * `delayMs` should match the overlay's transition duration (Blueprint's default
 * is 300ms).
 */
export const useDelayedUnmount = (isOpen: boolean, delayMs = 300) => {
  const [shouldRender, setShouldRender] = React.useState(isOpen);

  React.useEffect(() => {
    if (isOpen && !shouldRender) {
      setShouldRender(true);
      return undefined;
    }
    if (!isOpen && shouldRender) {
      const timeout = setTimeout(() => setShouldRender(false), delayMs);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [isOpen, shouldRender, delayMs]);

  return isOpen || shouldRender;
};
